import React from 'react';
import { motion } from 'framer-motion';
import { QrCode, SlidersHorizontal, CalendarHeart, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { scrollToSection } from '../../utils/scroll';

/* ──────────────────────────────────────────────────────────────────────────
   CÓMO FUNCIONA — 3 pasos para reducir fricción ("empezar es fácil") + CTA.
   ────────────────────────────────────────────────────────────────────────── */

const HowItWorks: React.FC = () => {
  const { t } = useTranslation();
  
  const steps = [
    {
      n: '01',
      icon: QrCode,
      accent: '#00e5d1',
      title: t('hiw.step1_title', 'Conecta tu WhatsApp'),
      desc: t('hiw.step1_desc', 'Escanea un código QR y tu número de la clínica queda enlazado. Sin cambiar de línea, sin apps nuevas.'),
    },
    {
      n: '02',
      icon: SlidersHorizontal,
      accent: '#8b5cf6',
      title: t('hiw.step2_title', 'Configura tu agente'),
      desc: t('hiw.step2_desc', 'Defines qué responder, tus horarios y tus servicios. El agente aprende el tono de tu clínica en minutos.'),
    },
    {
      n: '03',
      icon: CalendarHeart,
      accent: '#00e5d1',
      title: t('hiw.step3_title', 'Recibe citas en automático'),
      desc: t('hiw.step3_desc', 'Tu agente empieza a contestar, calificar pacientes y llenar tu agenda desde el primer minuto.'),
    },
  ];

  const scrollToCta = () => scrollToSection('#cta-final');

  return (
    <section id="como-funciona" style={{ padding: 'clamp(4.5rem, 9vh, 7.5rem) 0', position: 'relative' }}>
      <div className="container">
        {/* Encabezado */}
        <div style={{ textAlign: 'center', maxWidth: 700, margin: '0 auto 4.5rem' }}>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.6 }}
            style={{ fontSize: 'clamp(1.35rem, 5.6vw, 3.5rem)', fontWeight: 300, color: 'var(--text-primary)', marginBottom: '1.2rem', lineHeight: 1.12, letterSpacing: '-0.03em', whiteSpace: 'nowrap' }}
          >
            {t('hiw.title_start', 'En marcha en ')}
            <span
              style={{
                fontWeight: 800,
                background: 'linear-gradient(135deg, #00f0ff 0%, #9d6bff 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
              }}
            >
              {t('hiw.title_end', 'menos de un día.')}
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
            style={{ color: 'var(--text-secondary)', fontSize: '1.08rem', lineHeight: 1.6, margin: 0 }}
          >
            {t('hiw.subtitle', 'Sin instalaciones complicadas ni programadores. Tres pasos y tu agente está trabajando.')}
          </motion.p>
        </div>

        {/* Pasos */}
        <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '2rem' }}>
          {/* línea conectora (desktop) */}
          <div className="hiw-line" style={{ position: 'absolute', top: 40, left: '16%', right: '16%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', zIndex: 0 }} />

          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: i * 0.15, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                style={{ position: 'relative', zIndex: 1, textAlign: 'center', padding: '0 1rem' }}
              >
                <div style={{
                  width: 80, height: 80, borderRadius: '50%', margin: '0 auto 1.6rem',
                  display: 'grid', placeItems: 'center', position: 'relative',
                  background: `linear-gradient(135deg, ${s.accent}26, ${s.accent}08), var(--bg-dark)`,
                  border: `1px solid ${s.accent}55`,
                  boxShadow: `0 0 24px -6px ${s.accent}66`,
                }}>
                  <Icon size={32} color="var(--text-primary)" strokeWidth={1.8} />
                  <span style={{
                    position: 'absolute', top: -6, right: -6, fontSize: '0.7rem', fontWeight: 800,
                    color: '#fff', background: '#000', borderRadius: 999, padding: '3px 8px',
                    border: '2px solid var(--bg-dark, #000)',
                  }}>
                    {s.n}
                  </span>
                </div>
                <h3 style={{ fontSize: 'clamp(1.1rem, 3vw, 1.3rem)', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.7rem', letterSpacing: '-0.02em' }}>
                  {s.title}
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.96rem', lineHeight: 1.6, margin: '0 auto', maxWidth: 280 }}>
                  {s.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.6 }}
          style={{ textAlign: 'center', marginTop: '4rem' }}
        >
          <button
            className="btn-hiw-cta"
            onClick={scrollToCta}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '1rem 2rem', fontWeight: 600, cursor: 'pointer' }}
          >
            {t('hiw.btn_cta', 'Solicitar demo de 15 min')}
            <ArrowRight size={18} />
          </button>
        </motion.div>
      </div>

      <style>{`
        @media (max-width: 820px) { .hiw-line { display: none; } }

        /* Mismo lineamiento que .trial-btn del Hero: pill, sin movimiento,
           colores invertidos por tema. */
        .btn-hiw-cta {
          background: #fff;
          color: #000;
          border: none;
          border-radius: 980px;
          font-family: var(--font-body);
          transition: background 0.2s ease;
        }
        .btn-hiw-cta:hover { background: #e8e8ed; }
        [data-theme='light'] .btn-hiw-cta { background: #000; color: #fff; }
        [data-theme='light'] .btn-hiw-cta:hover { background: #1d1d1f; }
      `}</style>
    </section>
  );
};

export default HowItWorks;
